import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import cases from '../../../data/cases.json';
import { useEffect } from 'react';

const CasePage = () => {
  // Get slug from URL
  const slug = window.location.pathname.split('/').pop();

  // Find the case by slug
  const caseItem = cases.find(c => c.slug === slug);

  useEffect(() => {
    if (!caseItem) return;

    // Parse date from "29 Out 2024" format to ISO
    const parseDate = (dateStr: string): string => {
      const months: { [key: string]: number } = {
        'Jan': 0, 'Fev': 1, 'Mar': 2, 'Abr': 3, 'Mai': 4, 'Jun': 5,
        'Jul': 6, 'Ago': 7, 'Set': 8, 'Out': 9, 'Nov': 10, 'Dez': 11
      };
      const parts = dateStr.split(' ');
      const day = parseInt(parts[0]);
      const month = months[parts[1]];
      const year = parseInt(parts[2]);
      return new Date(year, month, day).toISOString().split('T')[0];
    };

    const jsonLdData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": caseItem.title,
      "description": caseItem.description,
      "image": `https://targetsoftware.com.br${caseItem.image}`,
      "author": {
        "@type": "Organization",
        "name": caseItem.author,
        "url": "https://targetsoftware.com.br"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Target Software",
        "logo": {
          "@type": "ImageObject",
          "url": "https://targetsoftware.com.br/images/logos/logo-target.png"
        }
      },
      "datePublished": parseDate(caseItem.date),
      "dateModified": parseDate(caseItem.date),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://targetsoftware.com.br/cases/${caseItem.slug}`
      },
      "articleSection": caseItem.category,
      "keywords": caseItem.tags.join(", "),
      "url": `https://targetsoftware.com.br/cases/${caseItem.slug}`,
      "wordCount": caseItem.content.reduce((count, block) => {
        if (block.type === 'paragraph' || block.type === 'heading') {
          return count + block.text.split(' ').length;
        }
        return count;
      }, 0),
      "timeRequired": `PT${caseItem.readTime.split(' ')[0]}M`,
      "inLanguage": "pt-BR",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Cases de Sucesso - Target Software",
        "url": "https://targetsoftware.com.br/cases"
      },
      "about": {
        "@type": "Organization",
        "name": caseItem.tags[0], // Company name from first tag
        "description": `Case de sucesso da ${caseItem.tags[0]} com a Target Software`
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [caseItem]);

  // If case not found, show 404
  if (!caseItem) {
    return (
      <div className="min-h-screen bg-white">
        <Header bannerHeightPercent={0.5} />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Case não encontrado</h1>
            <p className="text-gray-600 mb-8">O case que você está procurando não existe.</p>
            <a
              href="/"
              className="inline-flex items-center px-6 py-3 bg-[#7d2128] text-white font-semibold rounded-lg hover:bg-[#6b1c22] transition-colors duration-200"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Voltar para Home
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header bannerHeightPercent={0.5} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-[#161e2d] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 bg-[#7d2128] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-[#7d2128] rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-[#7d2128]/20 text-[#ff6b7a] font-medium rounded-full text-sm mb-4">
              {caseItem.category}
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {caseItem.title}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {caseItem.description}
            </p>
            <div className="flex items-center justify-center space-x-6 text-gray-400 text-sm">
              <div className="flex items-center">
                <i className="ri-building-line mr-2"></i>
                <span>{caseItem.tags[0]}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                <span>{caseItem.date}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-time-line mr-2"></i>
                <span>{caseItem.readTime} de leitura</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="relative -mt-16 mb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={caseItem.image}
              alt={caseItem.title}
              className="w-full h-[400px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          {caseItem.content.map((block, index) => {
            if (block.type === 'paragraph') {
              return (
                <p key={index} className="text-gray-700 leading-relaxed mb-6 text-lg">
                  {block.text}
                </p>
              );
            }
            if (block.type === 'heading') {
              return (
                <h3 key={index} className="text-xl font-bold text-gray-900 mt-12 mb-4">
                  {block.text}
                </h3>
              );
            }
            return null;
          })}
        </div>

        {/* Tags */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags:</h3>
          <div className="flex flex-wrap gap-2">
            {caseItem.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-[#7d2128]/10 hover:text-[#7d2128] transition-colors duration-200"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Compartilhe:</h3>
          <div className="flex space-x-3">
            <button
              onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`, '_blank')}
              className="w-10 h-10 bg-[#0077b5] text-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200"
            >
              <i className="ri-linkedin-fill"></i>
            </button>
            <button
              onClick={() => window.open(`https://twitter.com/intent/tweet?url=${window.location.href}&text=${caseItem.title}`, '_blank')}
              className="w-10 h-10 bg-[#1da1f2] text-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200"
            >
              <i className="ri-twitter-x-fill"></i>
            </button>
            <button
              onClick={() => window.open(`https://wa.me/?text=${caseItem.title} ${window.location.href}`, '_blank')}
              className="w-10 h-10 bg-[#25d366] text-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200"
            >
              <i className="ri-whatsapp-fill"></i>
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(window.location.href)}
              className="w-10 h-10 bg-gray-600 text-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200"
              title="Copiar link"
            >
              <i className="ri-link"></i>
            </button>
          </div>
        </div>
      </article>

      {/* Related Cases */}
      <section className="bg-gray-50 py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Outros Cases que Você Pode Gostar
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cases
              .filter(c => c.id !== caseItem.id)
              .slice(0, 3)
              .map((relatedCase) => (
                <article key={relatedCase.id} className="group cursor-pointer">
                  <a href={`/cases/${relatedCase.slug}`} className="block">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={relatedCase.image}
                          alt={relatedCase.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-medium text-[#7d2128] bg-[#7d2128]/10 px-3 py-1 rounded-full">
                            {relatedCase.category}
                          </span>
                          <span className="text-xs text-gray-500">{relatedCase.date}</span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-[#7d2128] transition-colors duration-300">
                          {relatedCase.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                          {relatedCase.description}
                        </p>
                        <div className="mt-4 flex items-center text-[#7d2128] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          Ver case completo
                          <i className="ri-arrow-right-line ml-1"></i>
                        </div>
                      </div>
                    </div>
                  </a>
                </article>
              ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#7d2128] to-[#6b1c22]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Quer saber mais sobre nossas soluções?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Entre em contato com nossos especialistas e descubra como podemos ajudar sua empresa.
          </p>
          <a
            href="/contato"
            className="inline-flex items-center px-8 py-4 bg-white text-[#7d2128] font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 hover:scale-105"
          >
            <i className="ri-mail-send-line text-xl mr-3"></i>
            Fale Conosco
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CasePage;